import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

// Extracted from the reference project's waba/lib.tsx: avatar helpers, the
// isolated-number wrapper and the 24h-window helpers.

export type WindowState = "open" | "closing" | "closed";

/**
 * Closed for 24h+ — the TS twin of the API's archive rule. A fresh chat with
 * no traffic yet reads as not archived (the server stays the authority).
 */
export function isConversationArchived(c: {
  windowExpiresAt: string | null;
  lastMessageAt: string | null;
}): boolean {
  const now = Date.now();
  const windowClosed =
    !c.windowExpiresAt || Date.parse(c.windowExpiresAt) <= now;
  if (!c.lastMessageAt) return false;
  return (
    windowClosed && Date.parse(c.lastMessageAt) <= now - 24 * 60 * 60 * 1000
  );
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((token) => token.replace(/[^\p{L}]/gu, ""))
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #f59e0b, #ef4444)",
  "linear-gradient(135deg, #10b981, #0d9488)",
  "linear-gradient(135deg, #f43f5e, #be123c)",
  "linear-gradient(135deg, #8b5cf6, #6366f1)",
  "linear-gradient(135deg, #0ea5e9, #2563eb)",
  "linear-gradient(135deg, #fb923c, #ea580c)",
  "linear-gradient(135deg, #14b8a6, #0891b2)",
  "linear-gradient(135deg, #ec4899, #db2777)",
];

export function avatarGradient(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length];
}

/** Renders a number LTR-isolated so it doesn't flip inside RTL text. */
export function Num({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      dir="ltr"
      className={cn("tw:inline-block tw:tabular-nums", className)}
      style={{ unicodeBidi: "isolate" }}
    >
      {children}
    </span>
  );
}

export function windowRemaining(
  iso: string | null,
): { hours: number; minutes: number } | null {
  if (!iso) return null;
  const diffMs = new Date(iso).getTime() - Date.now();
  if (diffMs <= 0) return null;
  const totalMinutes = Math.floor(diffMs / 60000);
  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
  };
}

export function windowState(
  windowOpen: boolean,
  windowExpiresAt: string | null,
): WindowState {
  if (!windowOpen) return "closed";
  const remaining = windowRemaining(windowExpiresAt);
  if (!remaining) return "closed";
  if (remaining.hours === 0) return "closing";
  return "open";
}

/** Window state derived from the summary DTO alone (no windowOpen flag). */
export function summaryWindowState(
  windowExpiresAt: string | null,
): WindowState {
  return windowState(!!windowExpiresAt, windowExpiresAt);
}

/**
 * WhatsApp-style clock, ported from the reference adapters.ts: "11:42 AM"
 * with a localized day-period marker when the locale has one.
 */
export function formatClock(date: Date, locale: string): string {
  const parts = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(date);
  const value = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";
  const clock = `${value("hour")}:${value("minute")}`;
  const marker = value("dayPeriod");
  return marker ? `${clock} ${marker}` : clock;
}

export function localeFor(language: string): string {
  return language === "ar" ? "ar-EH" : "en-US";
}

/**
 * Clock for a WhatsApp Unix-seconds timestamp. Outbound messages may not have
 * a WhatsApp timestamp yet — `fallbackIso` (the row's creation time) keeps a
 * sent message's clock showing, like the reference app.
 */
export function formatMessageClock(
  waTimestamp: number | null,
  language: string,
  fallbackIso?: string | null,
): string {
  const date = waTimestamp
    ? new Date(waTimestamp * 1000)
    : fallbackIso
      ? new Date(fallbackIso)
      : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return formatClock(date, localeFor(language));
}

/**
 * WhatsApp-style relative date for list rows, ported from the reference
 * adapters.ts (formatMessageDateLikeWhatsapp) but taking an ISO string:
 * time-of-day for today, "Yesterday", otherwise a plain date.
 */
export function formatConversationTime(
  iso: string | null,
  translator: (key: string) => string,
  language: string,
): string {
  if (!iso) return "-";
  const messageDate = new Date(iso);
  if (Number.isNaN(messageDate.getTime())) return "-";
  const today = new Date();

  const isSameDay = (d1: Date, d2: Date) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  if (isSameDay(messageDate, today)) {
    return formatClock(messageDate, localeFor(language));
  }

  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (isSameDay(messageDate, yesterday)) {
    return translator("Mandoob:Chats.Yesterday");
  }

  const day = messageDate.getDate();
  const month = messageDate.getMonth() + 1;
  const year = messageDate.getFullYear();
  return `${year}/${month}/${day}`;
}

/**
 * Day-divider label for the thread (reference formatMessageDateLikeWhatsapp
 * without time-of-day): "Today", "Yesterday", else "YYYY/M/D".
 */
export function formatDayDivider(
  date: Date,
  translator: (key: string) => string,
): string {
  const today = new Date();
  const isSameDay = (d1: Date, d2: Date) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  if (isSameDay(date, today)) return translator("Mandoob:Chats.Today");

  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (isSameDay(date, yesterday)) return translator("Mandoob:Chats.Yesterday");

  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
}

// The backend stores a media message's preview as the raw message type name
// ("Image", "Video"…) when there is no caption; localize those for the list
// rows. An outbound location's preview may carry the raw JSON payload — map
// that to the location label too instead of showing JSON.
const PREVIEW_TYPE_KEYS: Record<string, string> = {
  Image: "Mandoob:Message.Image",
  Sticker: "Mandoob:Message.Sticker",
  Video: "Mandoob:Message.Video",
  Audio: "Mandoob:Message.Audio",
  Document: "Mandoob:Message.Document",
  Location: "Mandoob:Message.Location",
  Contacts: "Mandoob:Message.Contacts",
};

// WhatsApp-style list previews pair the label with an emoji: "📍 موقع".
const PREVIEW_TYPE_EMOJI: Record<string, string> = {
  Image: "📷",
  Sticker: "💟",
  Video: "🎥",
  Audio: "🎤",
  Document: "📄",
  Location: "📍",
  Contacts: "👤",
};

/**
 * Meta template names are snake_case identifiers ("delivery_on_the_way"); the
 * underscores are an API detail, not something to show a driver.
 */
export function formatTemplateName(name: string): string {
  return name.replaceAll("_", " ").trim();
}

export function localizePreview(
  preview: string | null,
  translator: (key: string) => string,
): string {
  if (!preview) return "";
  // The backend stores media previews either bare ("Image") or bracketed
  // ("[Image]") — accept both.
  const token =
    preview.startsWith("[") && preview.endsWith("]")
      ? preview.slice(1, -1)
      : preview;
  const key = PREVIEW_TYPE_KEYS[token];
  if (key) {
    const emoji = PREVIEW_TYPE_EMOJI[token];
    return emoji ? `${emoji} ${translator(key)}` : translator(key);
  }
  if (preview.startsWith("{")) {
    try {
      const parsed = JSON.parse(preview) as {
        latitude?: unknown;
        longitude?: unknown;
      };
      if (
        typeof parsed?.latitude === "number" &&
        typeof parsed?.longitude === "number"
      ) {
        return `${PREVIEW_TYPE_EMOJI.Location} ${translator("Mandoob:Message.Location")}`;
      }
    } catch {
      // Not JSON — fall through to the raw preview text.
    }
  }
  return preview;
}
